import { SupportedCountryCurrency } from '../../types/supported-country-currency';
import { FxData } from './use-fx';

export const FALLBACK_FX_RATE_DATE = '2025-06-06';

/**
 * Fallback exchange rate data used when the API request fails or when offline.
 * Contains exchange rates for all supported currencies relative to each base currency.
 * The rates are fixed to a specific date (FALLBACK_FX_RATE_DATE) to ensure consistency.
 * To update these fallback rates:
 * 1. Open your browser's DevTools
 * 2. Go to Application tab > IndexedDB > seapoint > fxRates
 * 3. Copy the rates for the desired currency
 * 4. Replace the corresponding rates object in this file
 * Note: Update the FALLBACK_FX_RATE_DATE constant to match the date of the rates being used
 * This ensures we maintain accurate historical context for the fallback rates
 * @type {Record<SupportedCountryCurrency, FxData>}
 */
export const fallbackFxData: Record<SupportedCountryCurrency, FxData> = {
  EUR: {
    base: 'EUR' as SupportedCountryCurrency,
    rates: {
      [FALLBACK_FX_RATE_DATE]: {
        AED: 4.180589,
        AFN: 79.673621,
        ALL: 97.873417,
        AMD: 436.452071,
        ANG: 2.037368,
        AOA: 1037.982299,
        ARS: 1351.657441,
        AUD: 1.755111,
        AWG: 2.04875,
        AZN: 1.934931,
        BAM: 1.94889,
        BBD: 2.276389,
        BDT: 139.106785,
        BGN: 1.952089,
        BHD: 0.429066,
        BIF: 3388.578116,
        BMD: 1.138195,
        BND: 1.463727,
        BOB: 7.8636,
        BRL: 6.388116,
        BSD: 1.138195,
        BTC: 0.000010858493,
        BTN: 97.440697,
        BWP: 15.225076,
        BYN: 3.724276,
        BZD: 2.285953,
        CAD: 1.557856,
        CDF: 3305.96181,
        CHF: 0.937908,
        CLF: 0.027659,
        CLP: 1061.423301,
        CNH: 8.186797,
        CNY: 8.183163,
        COP: 4686.37936,
        CRC: 580.131126,
        CUC: 1.138195,
        CUP: 29.308509,
        CVE: 109.875454,
        CZK: 24.773942,
        DJF: 202.652304,
        DKK: 7.459333,
        DOP: 67.191766,
        DZD: 149.630476,
        EGP: 56.476529,
        ERN: 17.072918,
        ETB: 155.395503,
        EUR: 1,
        FJD: 2.562986,
        FKP: 0.841852,
        GBP: 0.841852,
        GEL: 3.107271,
        GGP: 0.841852,
        GHS: 11.664389,
        GIP: 0.841852,
        GMD: 79.673619,
        GNF: 9866.001977,
        GTQ: 8.746987,
        GYD: 238.448423,
        HKD: 8.931583,
        HNL: 29.682759,
        HRK: 7.530181,
        HTG: 149.232062,
        HUF: 403.131106,
        IDR: 18570.878249,
        ILS: 3.98627,
        IMP: 0.841852,
        INR: 97.626072,
        IQD: 1491.113619,
        IRR: 47917.98937,
        ISK: 144.004371,
        JEP: 0.841852,
        JMD: 181.785489,
        JOD: 0.80698,
        JPY: 165.13509448,
        KES: 147.123024,
        KGS: 99.535111,
        KHR: 4565.916151,
        KMF: 489.992583,
        KPW: 1024.37507,
        KRW: 1549.426661,
        KWD: 0.349047,
        KYD: 0.948354,
        KZT: 580.521395,
        LAK: 24569.393142,
        LBP: 101966.56817,
        LKR: 340.511003,
        LRD: 227.031177,
        LSL: 20.209845,
        LYD: 6.198025,
        MAD: 10.416867,
        MDL: 19.649559,
        MGA: 5133.766979,
        MKD: 61.490395,
        MMK: 2387.932107,
        MNT: 3867.584985,
        MOP: 9.197114,
        MRU: 45.199744,
        MUR: 51.913053,
        MVR: 17.533887,
        MWK: 1973.319394,
        MXN: 21.818864,
        MYR: 4.815132,
        MZN: 72.787541,
        NAD: 20.209845,
        NGN: 1775.6176,
        NIO: 41.891473,
        NOK: 11.51791,
        NPR: 155.935798,
        NZD: 1.894221,
        OMR: 0.437631,
        PAB: 1.138195,
        PEN: 4.126669,
        PGK: 4.678412,
        PHP: 63.605727,
        PKR: 321.038977,
        PLN: 4.2868,
        PYG: 9086.734996,
        QAR: 4.151082,
        RON: 5.040495,
        RSD: 117.166882,
        RUB: 90.167964,
        RWF: 1610.774918,
        SAR: 4.268986,
        SBD: 9.49303,
        SCR: 16.181321,
        SDG: 682.916713,
        SEK: 10.981498,
        SGD: 1.468681,
        SHP: 0.841852,
        SLL: 23867.370026,
        SOS: 650.496665,
        SRD: 42.047182,
        SSP: 148.261218,
        STD: 25361.022697,
        STN: 24.408741,
        SVC: 9.959688,
        SYP: 14798.805173,
        SZL: 20.203369,
        THB: 37.278146,
        TJS: 11.250736,
        TMT: 3.995063,
        TND: 3.376172,
        TOP: 2.740499,
        TRY: 44.641763,
        TTD: 7.701207,
        TWD: 34.108274,
        TZS: 3024.751942,
        UAH: 47.137374,
        UGX: 4120.362517,
        USD: 1.138195,
        UYU: 47.313379,
        UZS: 14543.640042,
        VES: 111.883529,
        VND: 29655.930149,
        VUV: 135.12873,
        WST: 3.186945,
        XAF: 655.957,
        XAG: 0.03167009,
        XAU: 0.00034216,
        XCD: 3.076028,
        XDR: 0.815843,
        XOF: 655.957,
        XPD: 0.00109069,
        XPF: 119.331742,
        XPT: 0.00097955,
        YER: 276.922671,
        ZAR: 20.231237,
        ZMW: 29.680812,
        ZWL: 366.498636,
      },
    },
  },
  GBP: {
    base: 'GBP' as SupportedCountryCurrency,
    rates: {
      [FALLBACK_FX_RATE_DATE]: {
        AED: 4.967439,
        AFN: 94.669404,
        ALL: 116.294679,
        AMD: 518.59897,
        ANG: 2.420832,
        AOA: 1233.346309,
        ARS: 1609.323245,
        AUD: 2.084784,
        AWG: 2.434356,
        AZN: 2.299114,
        BAM: 2.315701,
        BBD: 2.70484,
        BDT: 165.288792,
        BGN: 2.319502,
        BHD: 0.509823,
        BIF: 4026.359906,
        BMD: 1.35242,
        BND: 1.739223,
        BOB: 9.343649,
        BRL: 7.595597,
        BSD: 1.35242,
        BTC: 0.000012870519,
        BTN: 115.780514,
        BWP: 18.090667,
        BYN: 4.425241,
        BZD: 2.716204,
        CAD: 1.850969,
        CDF: 3928.193957,
        CHF: 1.114559,
        CLF: 0.03287,
        CLP: 1261.050497,
        CNH: 9.727326,
        CNY: 9.723359,
        COP: 5567.209847,
        CRC: 689.320601,
        CUC: 1.35242,
        CUP: 34.824815,
        CVE: 130.55568,
        CZK: 29.427439,
        DJF: 240.794541,
        DKK: 8.860391,
        DOP: 79.838275,
        DZD: 177.793201,
        EGP: 67.110867,
        ERN: 20.2863,
        ETB: 184.643294,
        EUR: 1.187847,
        FJD: 3.045379,
        FKP: 1,
        GBP: 1,
        GEL: 3.692107,
        GGP: 1,
        GHS: 13.859804,
        GIP: 1,
        GMD: 94.669402,
        GNF: 11722.933166,
        GTQ: 10.393303,
        GYD: 283.328032,
        HKD: 10.612507,
        HNL: 35.269504,
        HRK: 8.949058,
        HTG: 177.3198,
        HUF: 478.786593,
        IDR: 22067.943543,
        ILS: 4.736547,
        IMP: 1,
        INR: 116.011269,
        IQD: 1771.763815,
        IRR: 56936.881644,
        ISK: 171.054081,
        JEP: 1,
        JMD: 216.000275,
        JOD: 0.958866,
        JPY: 196.18272018,
        KES: 174.813808,
        KGS: 118.269128,
        KHR: 5425.290812,
        KMF: 582.216618,
        KPW: 1217.177992,
        KRW: 1840.779191,
        KWD: 0.414743,
        KYD: 1.126849,
        KZT: 689.784325,
        LAK: 29193.725525,
        LBP: 121158.222619,
        LKR: 404.600338,
        LRD: 269.761887,
        LSL: 24.013645,
        LYD: 7.364587,
        MAD: 12.377479,
        MDL: 23.347904,
        MGA: 6100.019778,
        MKD: 73.06382,
        MMK: 2837.377142,
        MNT: 4595.523131,
        MOP: 10.92815,
        MRU: 53.707021,
        MUR: 61.683877,
        MVR: 20.83403,
        MWK: 2344.728029,
        MXN: 25.903526,
        MYR: 5.721413,
        MZN: 86.48726,
        NAD: 24.013645,
        NGN: 2109.815759,
        NIO: 49.776084,
        NOK: 13.684949,
        NPR: 185.28528,
        NZD: 2.250015,
        OMR: 0.52,
        PAB: 1.35242,
        PEN: 4.90337,
        PGK: 5.558961,
        PHP: 75.57526,
        PKR: 381.463381,
        PLN: 5.092024,
        PYG: 10796.996323,
        QAR: 4.932378,
        RON: 5.987975,
        RSD: 139.188361,
        RUB: 107.141297,
        RWF: 1913.947185,
        SAR: 5.072474,
        SBD: 11.279762,
        SCR: 19.226891,
        SDG: 811.451995,
        SEK: 13.044796,
        SGD: 1.744899,
        SHP: 1,
        SLL: 28359.571012,
        SOS: 772.930003,
        SRD: 49.961099,
        SSP: 176.166228,
        STD: 30134.351767,
        STN: 29.002836,
        SVC: 11.834252,
        SYP: 17584.16473,
        SZL: 24.005949,
        THB: 44.293783,
        TJS: 13.368295,
        TMT: 4.746995,
        TND: 4.011618,
        TOP: 3.256303,
        TRY: 53.043801,
        TTD: 9.150691,
        TWD: 40.527968,
        TZS: 3594.056128,
        UAH: 56.009342,
        UGX: 4895.877228,
        USD: 1.35242,
        UYU: 56.218475,
        UZS: 17280.973652,
        VES: 132.941706,
        VND: 35237.625934,
        VUV: 160.562006,
        WST: 3.786776,
        XAF: 779.176758,
        XAG: 0.03765613,
        XAU: 0.00040663,
        XCD: 3.654983,
        XDR: 0.969397,
        XOF: 779.176758,
        XPD: 0.00129622,
        XPF: 141.747889,
        XPT: 0.00116373,
        YER: 329.043717,
        ZAR: 24.039351,
        ZMW: 35.267191,
        ZWL: 435.479237,
      },
    },
  },
};
